/* ============================================================
   deliveryAddressStrip — c3e7f6d0: pure view model for the
   delivery address strip on the provider order card (queue +
   detail) and admin order rows. Artem's call @b8810bf1:
   - самовывоз → адрес точки продавца/пункта (+ часы работы);
   - курьер продавца → адрес доставки клиента;
   - ПВЗ / почтовые службы (Ozon ПВЗ, Почта России, CDEK) →
     ТОЛЬКО название службы, без адреса.
   The data already rides the order delivery snapshot
   (ShippingAddress / PickupPointWorkingHours / DeliveryCarrier);
   DropoffPoint rows must never surface their point address even
   though ShippingAddress holds it. Zero runtime imports beyond
   the enum helpers — plain data in, plain data out — so the
   module runs under `node --test` (tests/deliveryAddressStrip.test.ts)
   as well as in the app bundle. Light theme, Montserrat, iOS-18
   minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import { normalizeDeliveryMethod, DELIVERY_METHOD_LABELS, type WebDeliveryMethod } from '../types/product.ts';
import { normalizeCarrierLabel } from './shippedTrackingView.ts';

/** One rendered address strip variant; null = nothing truthful to show. */
export type DeliveryAddressStrip =
  /** Самовывоз — where the customer collects (seller point / typed point address). */
  | { kind: 'point'; address: string; workingHours: string | null }
  /** Курьер продавца — the client's delivery address. */
  | { kind: 'address'; address: string }
  /** ПВЗ / почтовая служба — service name ONLY, never the point address. */
  | { kind: 'service'; serviceName: string };

/** 31c9ace7 parity: canonical «СДЭК» / «Почта России» / «Ozon» labels;
 *  unknown pass-through names stay honest, no link needed for the strip. */
function carrierServiceLabel(deliveryCarrier?: string | null): string | null {
  const carrier = normalizeCarrierLabel(deliveryCarrier);
  if (carrier == null) return null;
  return carrier === 'Cdek' ? 'СДЭК' : carrier === 'RussianPost' ? 'Почта России' : carrier === 'Ozon' ? 'Ozon' : carrier;
}

/** Pure derivation — trivially unit-testable, no React dependency.
 *  Legacy rows without a method snapshot normalize to Courier, so old
 *  rows with an address keep the address strip byte-identically. */
export function buildDeliveryAddressStrip(input: {
  deliveryMethod?: WebDeliveryMethod | number | null;
  shippingAddress?: string | null;
  pickupPointWorkingHours?: string | null;
  deliveryCarrier?: string | null;
}): DeliveryAddressStrip | null {
  const method = normalizeDeliveryMethod(input.deliveryMethod ?? null);
  const address = input.shippingAddress?.trim() ?? '';
  const hours = input.pickupPointWorkingHours?.trim() ?? '';

  if (method === 'Pickup') {
    // Самовывоз: the address IS the collection point (canonical point
    // address after OrderPickupPoints.ApplyTo, or the legacy typed one).
    return address
      ? { kind: 'point', address, workingHours: hours || null }
      : null;
  }
  if (method === 'DropoffPoint') {
    // ПВЗ/почта: service name only — the point address never renders,
    // even though the snapshot keeps it (delivery runs through the
    // carrier, the provider just hands the parcel over).
    return { kind: 'service', serviceName: carrierServiceLabel(input.deliveryCarrier) ?? DELIVERY_METHOD_LABELS.DropoffPoint };
  }
  // Courier (and legacy rows): the client's delivery address.
  return address ? { kind: 'address', address } : null;
}

/** Queue-row caption for the strip — one compact line per kind.
 *  📍 both address kinds (collection point / delivery address),
 *  📦 carrier service name (no address by design). */
export function deliveryAddressStripText(strip: DeliveryAddressStrip): string {
  switch (strip.kind) {
    case 'point':
      return strip.workingHours ? `📍 ${strip.address} · ${strip.workingHours}` : `📍 ${strip.address}`;
    case 'address':
      return `🚚 ${strip.address}`;
    case 'service':
      return `📦 ${strip.serviceName}`;
  }
}

/** Detail/admin row label for the strip («what this value is»). */
export function deliveryAddressStripLabel(strip: DeliveryAddressStrip): string {
  switch (strip.kind) {
    case 'point':
      return 'Пункт самовывоза';
    case 'address':
      return 'Адрес доставки';
    case 'service':
      return 'Доставка через';
  }
}

/** Detail/admin row value for the strip (no emoji there — labels carry
 *  the semantics; the queue row keeps the compact emoji form). */
export function deliveryAddressStripValue(strip: DeliveryAddressStrip): string {
  switch (strip.kind) {
    case 'point':
      return strip.workingHours ? `${strip.address} · ${strip.workingHours}` : strip.address;
    case 'address':
      return strip.address;
    case 'service':
      return strip.serviceName;
  }
}
